/**
 * projectileSystem.ts
 * Manages Star Shard projectile pool, firing, and updates.
 * Also exposes runCombatStep() which integrates enemy/combat logic via the pool.
 *
 * NO React imports, NO async.
 *
 * @module projectileSystem
 */

import { gameState } from './gameState';
import { inputState } from '../input/inputState';
import {
  PROJECTILE_BURST,
  PROJECTILE_COOLDOWN_S,
  MAX_PROJECTILES,
  NOVA_WIDTH,
  NOVA_HEIGHT,
} from '../../constants/gameConstants';
import { Projectile, PROJECTILE_WIDTH, PROJECTILE_HEIGHT } from '../entities/Projectile';
import { ObjectPool } from '../utils/objectPool';
import { stepCombat } from './combatStep';

/**
 * Module-level Star Shard pool — pre-warmed at module load.
 * Shared across all physics ticks (one game loop at a time).
 */
const projectilePool = new ObjectPool<Projectile>(
  () => new Projectile(),
  MAX_PROJECTILES,
);
projectilePool.prewarm(MAX_PROJECTILES);

/**
 * Return all active projectiles to the pool and clear the gameState list.
 * Call on level reset / PlayScene.init() so shards don't carry across sessions.
 */
export function resetProjectiles(): void {
  for (const shard of gameState.projectiles) {
    projectilePool.release(shard);
  }
  gameState.projectiles = [];
  gameState.attackCooldown = 0;
}

/**
 * Update the attack cooldown timer.
 * @param dt - Fixed timestep in seconds.
 */
export function updateAttackCooldown(dt: number): void {
  if (gameState.attackCooldown > 0) {
    gameState.attackCooldown -= dt;
    // Clamp to 0 to prevent negative drift from fixed-step subtraction and FP error.
    gameState.attackCooldown = Math.max(0, gameState.attackCooldown);
  }
}

/**
 * Fire a burst of Star Shards if attack conditions are met.
 * DECISION: ground attack fires shards; air attack remains stomp.
 *
 * @param novaX        - Nova's X position.
 * @param novaY        - Nova's Y position.
 * @param isGrounded   - Whether Nova is on ground.
 * @param facingRight  - Direction Nova is facing.
 */
export function tryFireProjectiles(
  novaX: number,
  novaY: number,
  isGrounded: boolean,
  facingRight: boolean,
): void {
  if (!inputState.attackPressed || !isGrounded || gameState.attackCooldown > 0) {
    return;
  }

  // DECISION: Suppress shard fire when a scene-layer system has claimed the
  // Attack input (e.g. the finale gate-open prompt). The engine reads only the
  // generic `gameState.suppressPlayerFire` flag — it never imports scene state.
  if (gameState.suppressPlayerFire) {
    return;
  }

  // DECISION: Pre-fire invariant — a non-default projectileKind must always
  // have ammo > 0 at the moment of firing. If an external seeding path
  // (dev toggle, future pickup-spawn, save restore, etc.) ever leaves the
  // pair (`projectileKind`, `ammo`) inconsistent, revert to the default
  // weapon before firing so we never burst with zero ammo. Also clamp ammo
  // to 0 to preserve the `star_shard => ammo === 0` contract used by the
  // HUD and other readers (a negative ammo state must not survive this path).
  if (gameState.projectileKind !== 'star_shard' && gameState.ammo <= 0) {
    gameState.projectileKind = 'star_shard';
    gameState.ammo = 0;
  }

  // Fire a burst — spawn up to PROJECTILE_BURST shards with slight vertical spread.
  // DECISION: compute offsets dynamically from burst count to avoid runtime assertions.
  // Shards spread ±PROJECTILE_HEIGHT/2 vertically, evenly spaced around center.
  const spawnX = facingRight
    ? novaX + NOVA_WIDTH + 2
    : novaX - PROJECTILE_WIDTH - 2;
  const spawnY = novaY + NOVA_HEIGHT / 2 - PROJECTILE_HEIGHT / 2;
  const spreadPx = PROJECTILE_HEIGHT; // total vertical spread in pixels

  // DECISION: Engine reads the kind off gameState (set by the scene layer)
  // rather than calling into `services/userSettings`. AGENTS.md forbids
  // services imports from the engine hot path.
  const kind = gameState.projectileKind;

  let firedCount = 0;
  for (let i = 0; i < PROJECTILE_BURST; i++) {
    // Map i from [0, burstCount - 1] to [-spreadPx/2, +spreadPx/2], centered for single shard.
    // DECISION: when PROJECTILE_BURST === 1, offsetY = 0 (centered). Otherwise spread evenly.
    let offsetY = 0;
    if (PROJECTILE_BURST > 1) {
      const t = i / (PROJECTILE_BURST - 1);
      offsetY = -spreadPx / 2 + t * spreadPx;
    }

    const shard = projectilePool.acquire();
    if (shard) {
      // DECISION: read the dev toggle once per burst (not per shard) so all
      // shards in one burst share the same kind. Reading inside the loop
      // would let a toggle flip mid-burst produce a mixed volley.
      shard.spawn(spawnX, spawnY + offsetY, facingRight, kind);
      gameState.projectiles.push(shard);
      firedCount++;
    }
  }

  // DECISION: Only apply cooldown if at least one shard was actually fired.
  // Prevents "dead" attack presses when pool is exhausted from triggering cooldown.
  if (firedCount > 0) {
    gameState.attackCooldown = PROJECTILE_COOLDOWN_S;
    // DECISION: Ammo is per-burst (one attack press = one ammo unit), so
    // matches the ROADMAP "Gem Shard: 10 shots" wording. We decrement after
    // the burst, not per shard. When ammo hits zero we auto-revert to the
    // default `star_shard` so the next attack press fires the default weapon.
    if (kind !== 'star_shard') {
      gameState.ammo = Math.max(0, gameState.ammo - 1);
      if (gameState.ammo === 0) {
        gameState.projectileKind = 'star_shard';
      }
    }
  }
}

/**
 * Update all active projectiles and release dead ones back to the pool.
 * @param dt - Fixed timestep in seconds.
 */
export function updateProjectiles(dt: number): void {
  const level = gameState.level;
  for (let i = gameState.projectiles.length - 1; i >= 0; i--) {
    const shard = gameState.projectiles[i];
    shard.update(dt, level);
    if (shard.dead) {
      projectilePool.release(shard);
      gameState.projectiles.splice(i, 1);
    }
  }
}

/**
 * Run one combat tick: update enemies and resolve combat interactions.
 * Wraps stepCombat() with the module-level projectile pool so physicsStep
 * doesn't need direct pool access.
 *
 * @param dt         - Fixed timestep in seconds.
 * @param wasFalling - True if Nova was falling (vy > 0) before collision resolution.
 */
export function runCombatStep(dt: number, wasFalling: boolean): void {
  const { nova, level } = gameState;
  stepCombat(dt, nova, level.platforms, level.width, level.height, projectilePool, wasFalling);
}
